import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { Lang } from '../types';
interface HeroProps {
  lang: Lang;
}
const content = {
  en: {
    headline: 'Fine Line Tattoos in Miami That Actually Look Clean.',
    subheading: 'Custom designs. Precise execution. Studio experience — no walk-ins, appointment only.',
    description: 'Julian Ospina (Barboxa Ink) is a 🇨🇴 tattoo artist based at Piajas Ink Tattoo Studio in Miami, specializing in Fine Line tattoos — minimal, elegant, and built to last.',
    quote: '"Your idea deserves more than a copy/paste design. Let\'s build something real."',
    cta: 'Book Your Session at Piajas Ink',
    ctaSub: 'Appointment only · Limited spots available',
    location: '📍 2116 SW 8th St, Miami FL 33135'
  },
  es: {
    headline: 'Tatuajes Fine Line en Miami que se ven realmente limpios.',
    subheading: 'Diseños personalizados. Ejecución precisa. Experiencia de estudio real — solo con cita.',
    description: 'Julián Ospina (Barboxa Ink) es un tatuador 🇨🇴 en Piajas Ink Tattoo Studio en Miami, especializado en tatuajes Fine Line — delicados, elegantes y hechos para durar.',
    quote: '"Tu idea merece más que un diseño copiado. Construyamos algo real."',
    cta: 'Reservar sesión en Piajas Ink',
    ctaSub: 'Solo con cita · Cupos limitados',
    location: '📍 2116 SW 8th St, Miami FL 33135'
  }
};
export function HeroSection({
  lang
}: HeroProps) {
  const t = content[lang];
  return <section className="relative bg-studio-bg pt-20 pb-0 overflow-hidden" data-id="element-200">
      {/* Banner Image — prominent at the top */}
      <div className="relative w-full bg-studio-bg" data-id="element-201">
        <img src="https://cdn.magicpatterns.com/uploads/szT5JqMPVFB1zeXbvLEi7r/heylloyd-logo_2-1773280994934.png" alt="Barboxa Ink — Julian Ospina tattooing at Piajas Ink Tattoo Studio, Miami" className="w-full h-auto md:h-[65vh] lg:h-[75vh] object-contain md:object-cover object-center" style={{
        filter: 'brightness(1.15) contrast(1.08) saturate(1.05)'
      }} data-id="element-202" />
        {/* Subtle gradient fade at bottom edge into dark bg */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-studio-bg via-studio-bg/50 to-transparent" data-id="element-203" />
        {/* Subtle vignette on sides — reduced opacity */}
        <div className="absolute inset-0 bg-gradient-to-r from-studio-bg/15 via-transparent to-studio-bg/15" data-id="element-204" />
      </div>

      {/* Text Content Below Image */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 w-full py-10 md:py-14 -mt-16" data-id="element-205">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.2
      }} className="max-w-3xl" data-id="element-206">
          <h1 className="text-3xl sm:text-4xl md:text-7xl font-bold leading-tight mb-6" data-id="element-207">
            {t.headline}
          </h1>

          <div className="space-y-3 sm:space-y-4 text-base sm:text-lg md:text-xl text-studio-muted font-light mb-6 sm:mb-8" data-id="element-208">
            <p className="text-studio-text font-medium" data-id="element-209">{t.subheading}</p>
            <p dangerouslySetInnerHTML={{
            __html: t.description.replace('Julian Ospina (Barboxa Ink)', '<strong class="text-studio-text">Julian Ospina (Barboxa Ink)</strong>').replace('Julián Ospina (Barboxa Ink)', '<strong class="text-studio-text">Julián Ospina (Barboxa Ink)</strong>').replace('Piajas Ink Tattoo Studio', '<strong class="text-studio-text">Piajas Ink Tattoo Studio</strong>').replace('Fine Line tattoos', '<strong class="text-studio-text">Fine Line tattoos</strong>').replace('tatuajes Fine Line', '<strong class="text-studio-text">tatuajes Fine Line</strong>')
          }} data-id="element-210" />
            <blockquote className="border-l-2 border-studio-accent pl-4 sm:pl-6 py-2 italic font-playfair text-lg sm:text-xl md:text-2xl text-studio-text mt-6 sm:mt-8" data-id="element-211">
              {t.quote}
            </blockquote>
          </div>

          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.8,
          delay: 0.6
        }} className="flex flex-col sm:flex-row items-start sm:items-center gap-6" data-id="element-212">
            <a href="#booking" className="group relative inline-flex items-center justify-center px-5 sm:px-8 py-3 sm:py-4 bg-studio-accent text-studio-bg font-semibold tracking-wide uppercase overflow-hidden transition-transform hover:scale-[1.02] active:scale-[0.98] text-sm sm:text-base" data-id="element-213">
              <span className="relative z-10 flex items-center gap-2 text-center" data-id="element-214">
                📅 {t.cta}
              </span>
              <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out" data-id="element-215" />
            </a>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3" data-id="element-216">
              <a href="https://www.google.com/maps/dir/?api=1&destination=2116+SW+8th+St,+Miami+FL+33135" target="_blank" rel="noopener noreferrer" className="group relative inline-flex items-center justify-center px-5 sm:px-8 py-3 sm:py-4 border-2 border-studio-accent text-studio-accent font-semibold tracking-wide uppercase overflow-hidden transition-all hover:bg-studio-accent hover:text-studio-bg hover:scale-[1.02] active:scale-[0.98] text-sm sm:text-base" data-id="element-217">
                <span className="relative z-10 flex items-center gap-2" data-id="element-218">
                  📍 {lang === 'en' ? 'Get Directions' : 'Cómo Llegar'}
                </span>
              </a>
              <div className="text-sm text-studio-muted space-y-1" data-id="element-219">
                <p data-id="element-220">{t.ctaSub}</p>
                <p className="text-studio-text" data-id="element-221">{t.location}</p>
              </div>
            </div>
          </motion.div>

          {/* Embedded Map */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.8
        }} className="mt-6 max-w-3xl" data-id="element-222">
            <div className="rounded-lg overflow-hidden border border-studio-border shadow-lg" data-id="element-223">
              <iframe title="Piajas Ink Tattoo Studio — 2116 SW 8th St, Miami FL 33135" src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3593.1!2d-80.2225!3d25.7653!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88d9b6843a26c7f1%3A0x0!2s2116+SW+8th+St%2C+Miami%2C+FL+33135!5e0!3m2!1sen!2sus!4v1700000000000" width="100%" height="250" style={{
              border: 0,
              filter: 'invert(0.9) hue-rotate(180deg) brightness(0.95) contrast(1.1)'
            }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" data-id="element-224" />
              <div className="bg-studio-bgAlt px-5 py-3 flex items-center justify-between" data-id="element-225">
                <div data-id="element-226">
                  <p className="text-studio-text font-semibold text-sm" data-id="element-227">
                    Piajas Ink Tattoo Studio
                  </p>
                  <p className="text-studio-muted text-xs mt-0.5" data-id="element-228">
                    2116 SW 8th St, Miami FL 33135
                  </p>
                </div>
                <a href="https://www.google.com/maps/dir/?api=1&destination=2116+SW+8th+St,+Miami+FL+33135" target="_blank" rel="noopener noreferrer" className="text-xs text-studio-accent font-medium uppercase tracking-wider hover:underline" data-id="element-229">
                  {lang === 'en' ? 'Open in Maps →' : 'Abrir en Maps →'}
                </a>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}